import { useRef, useState } from 'react'
import { Button } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { primary_color } from '../../../Helper/uiHelper'
import { request } from '../../../Redux/Sagas/requests/api'
import { fetchUpdateApiData } from '../../../Redux/Reducers/CMS'
import { isSuccessResponse } from '../../../helper'

const EMAIL_SPLIT = /[,;]+/

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const normalizeEmail = (value) => (value || '').trim().toLowerCase()

const parseChunk = (text) => {
  const parts = String(text || '').split(EMAIL_SPLIT)
  const remainder = parts.pop() ?? ''
  const emails = []
  const invalid = []
  parts.forEach((part) => {
    const email = normalizeEmail(part)
    if (!email) return
    if (isValidEmail(email)) {
      emails.push(email)
    } else {
      invalid.push(email)
    }
  })
  return { emails, invalid, remainder }
}

const AddEmailHoldForm = ({ gridRelatedData }) => {
  const dispatch = useDispatch()
  const inputRef = useRef(null)
  const [chips, setChips] = useState([])
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const addEmails = (incoming, extraInvalid = []) => {
    const next = []
    const seen = new Set(chips)
    incoming.forEach((email) => {
      if (!seen.has(email)) {
        seen.add(email)
        next.push(email)
      }
    })
    if (next.length) {
      setChips((current) => [...current, ...next])
    }
    if (extraInvalid.length) {
      setError('Not a valid email: ' + extraInvalid.join(', '))
    } else if (next.length) {
      setError('')
    }
  }

  const commitDraft = () => {
    const email = normalizeEmail(draft)
    if (!email) return
    if (!isValidEmail(email)) {
      setError('Not a valid email: ' + email)
      return
    }
    addEmails([email])
    setDraft('')
  }

  const onChange = (event) => {
    const value = event.target.value
    if (EMAIL_SPLIT.test(value)) {
      const { emails, invalid, remainder } = parseChunk(value)
      addEmails(emails, invalid)
      setDraft(remainder)
      return
    }
    setDraft(value)
    if (error) setError('')
  }

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitDraft()
    }
    if (event.key === 'Backspace' && !draft && chips.length) {
      setChips((current) => current.slice(0, -1))
    }
  }

  const removeChip = (email) => {
    setChips((current) => current.filter((item) => item !== email))
  }

  const onSubmit = () => {
    const pending = normalizeEmail(draft)
    const toSend = [...chips]
    if (pending) {
      if (!isValidEmail(pending)) {
        setError('Not a valid email: ' + pending)
        return
      }
      if (!toSend.includes(pending)) {
        toSend.push(pending)
      }
    }
    if (!toSend.length) {
      setError('Add at least one email.')
      return
    }

    setSaving(true)
    request('email-holds', 'POST', { emails: toSend })
      .then((res) => {
        if (isSuccessResponse(res, res?.data?.message || 'Emails added.')) {
          setChips([])
          setDraft('')
          setError('')
          gridRelatedData?.updateRefreshGrid?.(true)
          dispatch(fetchUpdateApiData(false))
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <div className='mb-3' style={{ maxWidth: '920px' }}>
      <div className='subtitle1 mb-1'>Add emails</div>
      <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>
        Paste or type emails, separated by commas. Click outside the box to add the last one — Enter is not needed.
      </div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          onClick={() => inputRef.current?.focus()}
          style={{
            flex: 1,
            minWidth: '280px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            padding: '6px 8px',
            background: '#fff',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            minHeight: '42px',
            cursor: 'text',
          }}>
          {chips.map((email) => (
            <span
              key={email}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: '#e8f7fc',
                border: '1px solid #b6e4f3',
                borderRadius: '16px',
                padding: '2px 8px',
                fontSize: '13px',
              }}>
              {email}
              <button
                type='button'
                onClick={(event) => {
                  event.stopPropagation()
                  removeChip(email)
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: '#049FDB',
                  fontSize: '14px',
                }}
                aria-label={'Remove ' + email}>
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            value={draft}
            onChange={onChange}
            onBlur={commitDraft}
            onKeyDown={onKeyDown}
            placeholder={chips.length ? 'Add another email' : 'parent@school.com, another@school.com'}
            style={{
              flex: 1,
              minWidth: '180px',
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              padding: '4px 2px',
            }}
          />
        </div>
        <Button
          color='primary'
          disabled={saving}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onSubmit}
          style={{ background: primary_color, borderColor: primary_color, minWidth: '110px', marginTop: '2px' }}>
          {saving ? 'Saving...' : 'Submit'}
        </Button>
      </div>
      {error ? <div className='text-danger mt-1' style={{ fontSize: '13px' }}>{error}</div> : null}
    </div>
  )
}

export default AddEmailHoldForm
